import type { StripeEmbeddedCheckoutDto } from '@/features/webinar/types';

// Page dédiée au paiement, chargée dans l'iframe du panneau depuis une autre origine que le live :
// Stripe.js n'y voit ni l'URL du live (qui porte le jeton du spectateur) ni la page parente.
export const STRIPE_CHECKOUT_FRAME_PATH = '/stripe-checkout.html';

type PageLocation = Pick<Location, 'protocol' | 'hostname' | 'port' | 'origin'>;

const LOCAL_HOSTS: Record<string, string> = { localhost: '127.0.0.1', '127.0.0.1': 'localhost' };

function toOrigin(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null;
  } catch {
    return null;
  }
}

function localTwinOrigin(location: PageLocation): string | null {
  const twin = LOCAL_HOSTS[location.hostname];
  return twin ? `${location.protocol}//${twin}${location.port ? `:${location.port}` : ''}` : null;
}

// Origine de la page de paiement vue du live. Jamais la même que le live : sans configuration,
// le paiement est indisponible (en local, localhost et 127.0.0.1 font deux origines distinctes).
export function resolveStripeCheckoutOrigin(
  configured: string | undefined,
  location: PageLocation,
): string | null {
  const origin = configured ? toOrigin(configured) : localTwinOrigin(location);
  return origin && origin !== location.origin ? origin : null;
}

// Origines du live autorisées à piloter la page de paiement, vue depuis cette page.
export function resolveStripeCheckoutParentOrigins(
  configured: string | undefined,
  location: PageLocation,
): string[] {
  const origins = (configured ?? '')
    .split(',')
    .map((value) => toOrigin(value))
    .filter((origin): origin is string => origin !== null);
  const localTwin = localTwinOrigin(location);
  if (!configured && localTwin) origins.push(localTwin);
  return [...new Set(origins)].filter((origin) => origin !== location.origin);
}

// Même version que l'API Stripe figée côté back (stripe-java, « clover »).
export const STRIPE_JS_URL = 'https://js.stripe.com/clover/stripe.js';

const MESSAGE_PREFIX = 'welya:stripe-checkout:';
const INIT_MESSAGE = `${MESSAGE_PREFIX}init`;

export type StripeCheckoutFrameEvent = 'ready' | 'mounted' | 'complete' | 'error';

const FRAME_EVENTS: readonly StripeCheckoutFrameEvent[] = ['ready', 'mounted', 'complete', 'error'];

export function buildStripeCheckoutInit(session: StripeEmbeddedCheckoutDto) {
  return {
    type: INIT_MESSAGE,
    clientSecret: session.clientSecret,
    stripeAccount: session.stripeAccount,
  };
}

export function readStripeCheckoutInit(data: unknown): StripeEmbeddedCheckoutDto | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, clientSecret, stripeAccount } = data as Record<string, unknown>;
  if (type !== INIT_MESSAGE) return null;
  if (typeof clientSecret !== 'string' || !clientSecret) return null;
  if (typeof stripeAccount !== 'string' || !stripeAccount.startsWith('acct_')) return null;
  return { clientSecret, stripeAccount };
}

export function buildStripeCheckoutFrameEvent(event: StripeCheckoutFrameEvent) {
  return { type: `${MESSAGE_PREFIX}${event}` };
}

export function readStripeCheckoutFrameEvent(data: unknown): StripeCheckoutFrameEvent | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type } = data as { type?: unknown };
  if (typeof type !== 'string' || !type.startsWith(MESSAGE_PREFIX)) return null;
  const event = type.slice(MESSAGE_PREFIX.length);
  return FRAME_EVENTS.find((candidate) => candidate === event) ?? null;
}

// Le secret de paiement ne circule qu'entre le live et la page dédiée : on vérifie à la fois la
// fenêtre émettrice et son origine, dans les deux sens.
export function isTrustedStripeCheckoutMessage(
  event: Pick<MessageEvent, 'origin' | 'source'>,
  expectedSource: MessageEventSource | null | undefined,
  expectedOrigins: string | readonly string[],
): boolean {
  const origins = typeof expectedOrigins === 'string' ? [expectedOrigins] : expectedOrigins;
  return !!expectedSource && event.source === expectedSource && origins.includes(event.origin);
}

export type StripeCheckoutFailure = 'unavailable' | 'retry';

const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);

// 4xx : offre retirée, prix archivé, compte déconnecté, live terminé. Réessayer n'y changerait
// rien. Le reste (réseau, Stripe saturé) peut passer au second essai.
export function resolveStripeCheckoutFailure(error: unknown): StripeCheckoutFailure {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  if (typeof status !== 'number' || RETRYABLE_CLIENT_STATUSES.has(status)) return 'retry';
  return status >= 400 && status < 500 ? 'unavailable' : 'retry';
}
